import type { CatalogItem } from '@viva/core';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SportsChannel, SportsMatch, SportsToday } from '../account/api';
import { useAccount } from '../account/AccountGate';
import { groupByLeague, readFavouriteLeagues, saveFavouriteLeagues } from './league-groups';
import type { LeagueLine } from './match-days';
import { bigMatchToday, dayStart, featuredToday, leagueLines, matchesOnDay, nextDay, pickerDays, upcomingHighlights } from './match-days';
import { onBack } from '../spatial-navigation';
import { useCatalog } from '../state/catalog-context';
import { usePlay } from '../state/playback-context';
import { language as appLanguage, languageName, t, useLanguage } from '../i18n';
import type { MessageKey } from '../i18n/en';
import { Icon } from '../components/Icon';
import { heroImage } from './hero-images';
import { groupMatchQualities, type Upcoming } from './match-qualities';
import { readSportsCache, saveSportsCache } from './sports-cache';
import { findExtraChannels, searchHintNames } from './more-channels';

/** The list is refreshed this often: channels are re-tested during matches. */
const REFRESH_MS = 60 * 1000;
/** Each slide shows this long before the next. */
const SLIDE_MS = 7000;

/** Browser language → commentary language, after the ones for the viewer's country. */
const BROWSER_LANGUAGES: Record<string, string> = {
  ar: 'Arabic', fr: 'French', en: 'English', es: 'Spanish', de: 'German', it: 'Italian', pt: 'Portuguese', nl: 'Dutch', tr: 'Turkish', nb: 'Norwegian', no: 'Norwegian', sv: 'Swedish',
};

/** A commentary language as shown ("Spanish" → "Español"; channels with none: "Other"). */
const commentary = (name: string) => (name === 'Other' ? t('browse.other') : languageName(name));
/** The best picture first (owner's choice): 4K, full HD, HD, SD, then channels that don't say; unprocessed "RAW" feeds last. */
const QUALITY_ORDER: Record<string, number> = { UHD: 0, FHD: 1, HD: 2, SD: 3, none: 4 };
const RAW = /\bRAW\b|ᴿᴬᵂ/i;

function weight(channel: SportsChannel): number {
  return (RAW.test(channel.label) ? 10 : 0) + QUALITY_ORDER[channel.quality ?? 'none']!;
}

/**
 * The first screen: a slider of today's best football matches for this
 * viewer's country, then what is live now, then the rest of the day by
 * league. Only channels the relay tested and found playing smoothly are
 * offered; Live TV, Movies and Series are in the menu.
 */
export function HomeScreen({ onSearch }: { onSearch?: () => void } = {}) {
  const { sports, account } = useAccount();
  useLanguage();
  // The last list shows at once while a fresh one loads (the call can take seconds).
  const [today, setToday] = useState<SportsToday | null>(() => {
    const saved = readSportsCache(account.username);
    return saved ? groupMatchQualities(saved) : null;
  });
  const [failed, setFailed] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  /** The day picker's choice: 0 is today (#93). */
  const [dayIndex, setDayIndex] = useState(0);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [favourites, setFavourites] = useState<string[]>(readFavouriteLeagues);
  const toggleFavourite = useCallback((league: string) => {
    setFavourites((current) => {
      const next = current.includes(league) ? current.filter((name) => name !== league) : [...current, league];
      saveFavouriteLeagues(next);
      return next;
    });
  }, []);
  /** The button that opened the channel list: the highlight goes back to it when the list closes. */
  const opener = useRef<HTMLElement | null>(null);
  const close = useCallback(() => {
    setOpenId(null);
    const button = opener.current;
    if (button) setTimeout(() => button.isConnected && button.focus({ preventScroll: true }), 0);
  }, []);
  // Back on the remote closes a match's channel list first.
  useEffect(() => (openId ? onBack(() => (close(), true)) : undefined), [openId, close]);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    const load = () =>
      void sports().then((result) => {
        if (!active) return;
        if (result.ok) {
          saveSportsCache(account.username, result.data);
          setToday(groupMatchQualities(result.data));
          setFailed(false);
        } else setFailed(true);
      });
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [sports, account.username]);

  const languages = useMemo(() => preferredLanguages(today?.languages ?? []), [today?.languages]);
  const open = useCallback((id: string) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOpenId(id);
    // Bring the channel list into view (it opens under its match).
    setTimeout(() => panelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }), 0);
  }, []);

  /** Shows a coming day's matches (from a coming match's slide or a league's "next" line). */
  const showDay = useCallback((at: number) => {
    const index = pickerDays(Date.now()).indexOf(dayStart(at));
    if (index < 0) return;
    setOpenId(null);
    setDayIndex(index);
    setTimeout(() => {
      pickerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      pickerRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus({ preventScroll: true });
    }, 0);
  }, []);

  if (!today) {
    return (
      <section className="screen home">
        <p className="muted">{failed ? t('home.failed') : t('home.loading')}</p>
      </section>
    );
  }
  const now = Date.now();
  const picked = (today.featured ?? []).map((id) => today.matches.find((match) => match.id === id)).filter((match): match is SportsMatch => Boolean(match));
  // "Today" is the viewer's own day (device time zone): tomorrow morning's games wait for tomorrow.
  const featured = featuredToday(picked, now);
  const schedule = today.schedule ?? [];
  // No big match today: the slider shows the coming days' big matches instead (#93).
  const upcoming = !bigMatchToday(picked, today.bigToday, now) ? upcomingHighlights(schedule, now) : [];
  const days = pickerDays(now);
  const day = days[Math.min(dayIndex, days.length - 1)]!;
  const dayMatches = matchesOnDay(today.matches, schedule, day, now);
  // Live matches stay under their own league (first in it), not in a group of their own (Abel, 2026-10-29).
  const leagues = groupByLeague(dayMatches, favourites);
  const lines = leagueLines(dayMatches, schedule, day, favourites).filter((line) => days.includes(line.nextDay));
  const ranked = leagues.filter((group) => group.league !== '');
  const other = leagues.find((group) => group.league === '');
  // A coming match with expected channels opens too (#111): its panel lists them, not playable yet.
  const opened = today.matches.find((match) => match.id === openId) ?? dayMatches.find((match) => match.id === openId && hasExpected(match)) ?? null;
  const fromSlider = (match: SportsMatch) => (match.scheduled && match.startsAt !== null ? showDay(match.startsAt) : open(match.id));
  // The channel list opens right under its match; a match not in the day's list (from the slider) gets it above the days.
  const inList = opened !== null && dayMatches.some((match) => match.id === opened.id);
  const channels = (inline: boolean) =>
    opened && today.serverUrl ? (
      <div ref={panelRef}>
        <MatchChannels key={opened.id} match={opened} serverUrl={today.serverUrl} languages={languages} inline={inline} onClose={close} onSearch={onSearch} />
      </div>
    ) : null;
  const panel = inList ? channels(true) : null;
  const list = (group: { league: string; matches: SportsMatch[] }) => (
    <MatchList
      key={group.league || '-'}
      league={group.league}
      favourite={favourites.includes(group.league)}
      onFavourite={toggleFavourite}
      matches={group.matches}
      openId={openId}
      panel={panel}
      onOpen={open}
    />
  );

  return (
    <section className="screen home">
      {upcoming.length > 0 ? (
        <MatchSlider matches={upcoming} serverUrl={today.serverUrl ?? ''} languages={languages} label={t('home.comingUp')} onOpen={fromSlider} />
      ) : featured.length > 0 && today.serverUrl ? (
        <MatchSlider matches={featured} serverUrl={today.serverUrl} languages={languages} label={t('home.todaysMatches')} onOpen={fromSlider} />
      ) : (
        <div className="home-empty panel">
          <h1>{t('home.welcome')}</h1>
          <p className="muted">{t('home.noMatches')}</p>
        </div>
      )}
      {inList ? null : channels(false)}
      <DayPicker days={days} chosen={day} pickerRef={pickerRef} onChoose={(index) => (setOpenId(null), setDayIndex(index))} />
      <section className="sports-leagues" aria-label={dayName(day, days[0]!)}>
        {ranked.map(list)}
        {lines.length > 0 ? <LeagueLines lines={lines} today={day === days[0]} onShowDay={showDay} /> : null}
        {other ? list(other) : null}
        {/* Without a schedule (a relay that doesn't send one yet, or FotMob failing) later days are unknown, not empty. */}
        {dayMatches.length === 0 ? <p className="muted">{t(schedule.length === 0 && day !== days[0] ? 'home.noSchedule' : 'home.noMatchesDay')}</p> : null}
      </section>
    </section>
  );
}

/** The viewer's languages: their country's, then their browser's. */
function preferredLanguages(fromCountry: string[]): string[] {
  const list = [...fromCountry];
  // The app's language first (e.g. chosen on the website), then the browser's.
  const languages = [appLanguage(), ...(typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]))];
  for (const code of languages) {
    const language = BROWSER_LANGUAGES[code.slice(0, 2).toLowerCase()];
    if (language && !list.includes(language)) list.push(language);
  }
  return list;
}

/** The language to start with: the viewer's first one that has channels, else the most offered. */
function startLanguage(match: SportsMatch, languages: string[]): string | null {
  const offered = match.channels.map((channel) => channel.language ?? 'Other');
  const mine = languages.find((language) => offered.includes(language));
  if (mine) return mine;
  const counts = new Map<string, number>();
  for (const language of offered) counts.set(language, (counts.get(language) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

/** A language's channels: ones already playing first (they share a connection and start at once), then by quality. */
export function channelsIn(match: Pick<SportsMatch, 'channels'>, language: string | null): SportsChannel[] {
  const playing = (channel: SportsChannel) => ((channel.watching ?? 0) > 0 ? 0 : 1);
  // Channels viewers confirmed for the match next (#156).
  const confirmed = (channel: SportsChannel) => (channel.confirmed ? 0 : 1);
  return match.channels
    .filter((channel) => (channel.language ?? 'Other') === language)
    .sort((a, b) => playing(a) - playing(b) || confirmed(a) - confirmed(b) || weight(a) - weight(b));
}

/**
 * The remote on the slide's outermost button: ◀ ▶ go to the previous or next match, but not round
 * from the first to the last (or back): at the ends the key is left to the screen, so ◀ reaches the
 * side menu (#187). Returns the step, or null when the slider doesn't take the key.
 */
export function remoteSlideStep(key: string, current: number, count: number): -1 | 1 | null {
  if (key !== 'ArrowLeft' && key !== 'ArrowRight') return null;
  const step = key === 'ArrowRight' ? 1 : -1;
  const next = current + step;
  return next < 0 || next >= count ? null : step;
}

/** One slide per match, moving on by itself; paused while the viewer interacts. */
function MatchSlider({
  matches,
  serverUrl,
  languages,
  label,
  onOpen,
}: {
  matches: SportsMatch[];
  serverUrl: string;
  languages: string[];
  label: string;
  onOpen: (match: SportsMatch) => void;
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);
  const count = matches.length;
  const current = Math.min(index, count - 1);
  const go = useCallback((step: number) => setIndex((value) => (Math.min(value, count - 1) + step + count) % count), [count]);
  const root = useRef<HTMLDivElement>(null);
  /** Set when the remote changed the slide: the highlight moves onto the new slide's main button. */
  const followFocus = useRef(false);
  useEffect(() => {
    if (!followFocus.current) return;
    followFocus.current = false;
    root.current?.querySelector<HTMLElement>('.slide[aria-hidden="false"] [data-autofocus]')?.focus({ preventScroll: true });
  }, [current]);

  useEffect(() => {
    const still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (paused || count < 2 || still) return undefined;
    const timer = setTimeout(() => go(1), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [current, paused, count, go]);

  return (
    <div
      className="slider"
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      ref={root}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(event) => {
        // Remote: ◀ ▶ move between the slide's buttons, and past the first or last one to the previous or next match.
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const buttons = [...(event.target as HTMLElement).closest('.slide__actions')?.querySelectorAll('button') ?? []];
        const position = buttons.indexOf(event.target as HTMLButtonElement);
        if (position < 0 || buttons[position + (event.key === 'ArrowRight' ? 1 : -1)]) return;
        const step = remoteSlideStep(event.key, current, count);
        if (step === null) return;
        event.preventDefault();
        followFocus.current = true;
        go(step);
      }}
      onTouchStart={(event) => {
        touchStart.current = event.touches[0]?.clientX ?? null;
        setPaused(true);
      }}
      onTouchEnd={(event) => {
        const start = touchStart.current;
        const end = event.changedTouches[0]?.clientX;
        if (start !== null && end !== undefined && Math.abs(end - start) > 40) go(end < start ? 1 : -1);
        touchStart.current = null;
        setPaused(false);
      }}
    >
      <div className="slider__track" style={{ transform: `translateX(-${current * 100}%)` }}>
        {matches.map((match, position) => (
          <Slide key={match.id} match={match} serverUrl={serverUrl} languages={languages} hidden={position !== current} label={t('home.slideOf', { position: position + 1, count })} onOpen={() => onOpen(match)} />
        ))}
      </div>
      {count > 1 ? (
        <>
          {/* Mouse and touch; the remote changes slides with ◀ ▶ on the slide's buttons. */}
          <button type="button" className="slider__arrow slider__arrow--prev" aria-label={t('home.previousMatch')} tabIndex={-1} onClick={() => go(-1)}>
            <Icon name="back" />
          </button>
          <button type="button" className="slider__arrow slider__arrow--next" aria-label={t('home.nextMatch')} tabIndex={-1} onClick={() => go(1)}>
            <Icon name="next" />
          </button>
          <div className="slider__dots">
            {matches.map((match, position) => (
              <button key={match.id} type="button" className="slider__dot" tabIndex={-1} aria-label={`${match.home} vs ${match.away}`} aria-current={position === current} onClick={() => setIndex(position)} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Slide({ match, serverUrl, languages, hidden, label, onOpen }: { match: SportsMatch; serverUrl: string; languages: string[]; hidden: boolean; label: string; onOpen: () => void }) {
  const play = usePlay();
  const { sources } = useCatalog();
  const language = startLanguage(match, languages);
  // Live with nothing checked yet: the channels that might have it, playable at once (#152).
  const checked = channelsIn(match, language);
  const channels = checked.length > 0 ? checked : mightHaveChannels(match, languages);
  const sourceId = sources?.[0]?.id ?? 'sports';
  // The league heading ("Botola Pro") rather than FotMob's name with the country ("Morocco Botola Pro").
  const hero = heroImage(match.league || match.competition, [match.competition, match.home, match.away]);
  const watch = () => {
    const items = channels.map((channel) => channelItem(serverUrl, sourceId, match, channel));
    if (items[0]) play({ item: items[0], queue: { items, index: 0 } });
  };
  return (
    <article className={`slide${hero ? ' slide--photo' : ''}`} style={slideStyle(match.competition, hero)} aria-roledescription="slide" aria-label={label} aria-hidden={hidden}>
      <div className="slide__body">
      <div className="slide__top">
        <span className="league-dot" aria-hidden="true" />
        <span className="slide__competition">{match.unconfirmed ? t('home.notConfirmed') : match.competition || t('home.football')}</span>
        <When match={match} long />
      </div>
      <h2 className="slide__matchup" aria-label={`${match.home} vs ${match.away}`}>
        <span className="slide__team">
          <TeamPicture name={match.home} src={match.homeCrest} size="large" />
          <span className="slide__name">{match.home}</span>
        </span>
        {match.liveScore && match.liveScore.home !== null ? (
          <span className="slide__score">
            <span className="slide__goals">
              {match.liveScore.home} – {match.liveScore.away}
            </span>
            <MatchClock score={match.liveScore} />
          </span>
        ) : (
          <span className="slide__vs" aria-hidden="true">
            –
          </span>
        )}
        <span className="slide__team slide__team--away">
          <TeamPicture name={match.away} src={match.awayCrest} size="large" />
          <span className="slide__name">{match.away}</span>
        </span>
      </h2>
      <p className="slide__info">
        {expectedText(match) ??
          mightBeOnText(match) ??
          (match.scheduled
            ? t('home.channelsOnMatchDay')
            : match.checking
              ? t('home.checking')
              : checked.length > 0
                ? `${t(channels.length === 1 ? 'home.channelIn' : 'home.channelsIn', { count: channels.length, language: commentary(language ?? 'Other') })}${match.channels.length > channels.length ? ` · ${t('home.moreOther', { count: match.channels.length - channels.length })}` : ''}`
                : match.live
                  ? t('home.noChannel')
                  : t('home.atKickoff'))}
      </p>
      </div>
      <div className="slide__actions">
        {channels.length > 0 ? (
          <button type="button" className="button primary" tabIndex={hidden ? -1 : 0} data-autofocus={hidden ? undefined : true} onClick={watch}>
            <Icon name="play" />
            {t('home.watchNow')}
          </button>
        ) : null}
        <button type="button" className="button" tabIndex={hidden ? -1 : 0} data-autofocus={hidden || channels.length > 0 ? undefined : true} onClick={onOpen}>
          <Icon name="list" />
          {match.scheduled && match.startsAt !== null
            ? t('home.seeDay', { day: dayName(dayStart(match.startsAt), dayStart(Date.now())) })
            : match.channels.length > 0
              ? t('home.allChannels')
              : t('home.details')}
        </button>
      </div>
    </article>
  );
}

/** Words that don't help tell clubs apart in initials ("FC Barcelona" → BAR). */
const CLUB_WORDS = /^(fc|cf|sc|ac|afc|cd|ud|sd|sk|fk|as|ss|club|de|the)$/i;

/** "Real Madrid" → RM, "Arsenal" → ARS, "Paris Saint-Germain" → PSG. */
function initials(name: string): string {
  const words = name.replace(/\(.*?\)/g, ' ').split(/[\s.\-']+/).filter((word) => word && !CLUB_WORDS.test(word));
  const all = words.length > 0 ? words : name.split(/\s+/);
  if (all.length === 1) return all[0]!.slice(0, 3).toUpperCase();
  return all
    .slice(0, 3)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

/** A team's flag or crest; a badge with its initials when there's none or it doesn't load. */
function TeamPicture({ name, src, size }: { name: string; src: string | null; size: 'large' | 'small' }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img className={`team-picture team-picture--${size}`} src={src} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
  }
  return (
    <span className={`team-picture team-picture--${size} team-picture--badge`} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/** A fixed palette for competitions: calm colours that sit well next to the brand red. */
const LEAGUE_COLOURS = ['#7b5bd6', '#3b6fd8', '#1f9e8f', '#3e9b4f', '#c98a1b', '#d0652c', '#c24d72', '#5b6b86'];

/** The same competition always gets the same colour, shown as a thin line and a dot. */
export function leagueColour(competition: string): string {
  let hash = 0;
  for (const char of competition) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return LEAGUE_COLOURS[hash % LEAGUE_COLOURS.length]!;
}

function leagueStyle(competition: string): CSSProperties {
  return { '--league': leagueColour(competition) } as CSSProperties;
}

/** The competition's colour, and its photo behind the slide when there is one. */
function slideStyle(competition: string, hero: string | null): CSSProperties {
  if (!hero) return leagueStyle(competition);
  // A full address: a relative url() in a CSS variable resolves against the stylesheet, not the page.
  const address = new URL(`${import.meta.env.BASE_URL}${hero}`, document.baseURI).href;
  return { ...leagueStyle(competition), '--hero': `url("${address}")` } as CSSProperties;
}

function MatchList({
  league,
  favourite,
  onFavourite,
  matches,
  openId,
  panel,
  onOpen,
}: {
  league: string;
  favourite: boolean;
  onFavourite: (league: string) => void;
  matches: SportsMatch[];
  openId: string | null;
  /** The open match's channel list, shown under that match's row. */
  panel: ReactNode;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="sports-list">
      <div className="sports-list__head">
        <h2>{league || t('home.otherLeagues')}</h2>
        {league ? (
          <button
            type="button"
            className="sports-list__star"
            aria-pressed={favourite}
            aria-label={t(favourite ? 'home.unfavouriteLeague' : 'home.favouriteLeague', { league })}
            title={t(favourite ? 'home.unfavouriteLeague' : 'home.favouriteLeague', { league })}
            onClick={() => onFavourite(league)}
          >
            <Icon name="star" filled={favourite} />
          </button>
        ) : null}
      </div>
      {matches.map((match) => {
        const scored = match.liveScore && match.liveScore.home !== null ? match.liveScore : null;
        const content = (
          <>
            <span className="sports-match__when">
              <When match={match} />
            </span>
            <span className="sports-match__home">
              <span className="sports-match__name">{match.home}</span>
              <TeamPicture name={match.home} src={match.homeCrest} size="small" />
            </span>
            {scored ? (
              <span className="sports-match__score" aria-label={`${scored.home} – ${scored.away}`}>
                <span>{scored.home}</span>
                <span className="sports-match__dash" aria-hidden="true">
                  –
                </span>
                <span>{scored.away}</span>
              </span>
            ) : (
              <span className="sports-match__score sports-match__score--none muted">vs</span>
            )}
            <span className="sports-match__away">
              <TeamPicture name={match.away} src={match.awayCrest} size="small" />
              <span className="sports-match__name">{match.away}</span>
            </span>
            <span className="sports-match__count muted">
              {/* Under "Other" the heading doesn't say which league it is. */}
              {!league && match.competition ? (
                <span className="sports-match__competition">
                  <span className="league-dot" style={leagueStyle(match.competition)} aria-hidden="true" />
                  {match.competition}
                </span>
              ) : null}
              {match.unconfirmed ? <span className="sports-match__competition">{t('home.notConfirmed')}</span> : null}
              <span>
                <ChannelCount match={match} />
              </span>
            </span>
          </>
        );
        // A coming match has no channels to open; it still takes the highlight so the remote can scroll the list.
        if (match.scheduled && !hasExpected(match)) {
          return (
            <div key={match.id} className="sports-match sports-match--scheduled" tabIndex={0}>
              {content}
            </div>
          );
        }
        const row = (
          <button key={match.id} type="button" className="sports-match" aria-expanded={openId === match.id} onClick={() => onOpen(match.id)}>
            {content}
          </button>
        );
        // The open match and its channels share one raised card.
        return openId === match.id && panel ? (
          <div key={match.id} className="sports-match-open">
            {row}
            {panel}
          </div>
        ) : (
          row
        );
      })}
    </div>
  );
}

/** "Today", "Tomorrow", then "Thu 30". */
export function dayName(day: number, today: number): string {
  if (day === today) return t('home.today');
  if (day === nextDay(today)) return t('home.tomorrow');
  return new Date(day).toLocaleDateString(appLanguage(), { weekday: 'short', day: 'numeric' });
}

/** "Sat 1 Nov". */
function dateText(at: number): string {
  return new Date(at).toLocaleDateString(appLanguage(), { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Today and the six days after it; one button each, so the remote moves along them with ◀ ▶. */
function DayPicker({ days, chosen, pickerRef, onChoose }: { days: number[]; chosen: number; pickerRef: RefObject<HTMLDivElement>; onChoose: (index: number) => void }) {
  return (
    <div className="day-picker" role="group" aria-label={t('home.days')} ref={pickerRef}>
      {days.map((day, index) => (
        <button key={day} type="button" className="day-picker__day" aria-pressed={day === chosen} onClick={() => onChoose(index)}>
          {dayName(day, days[0]!)}
        </button>
      ))}
    </div>
  );
}

/** Ranked leagues without a match on the chosen day: one line each, with their next match day (a button to it). */
function LeagueLines({ lines, today, onShowDay }: { lines: LeagueLine[]; today: boolean; onShowDay: (at: number) => void }) {
  return (
    <div className="sports-list league-lines">
      {lines.map((line) => (
        <button key={line.league} type="button" className="league-line" onClick={() => onShowDay(line.next)}>
          <span className="league-line__name">{line.league}</span>
          <span className="muted">{t(today ? 'home.noMatchToday' : 'home.noMatchDay', { date: dateText(line.next) })}</span>
        </button>
      ))}
    </div>
  );
}

/** The match minute, counted on from the last fetched score ("67'", "45+'", "HT", "FT"). */
function MatchClock({ score }: { score: NonNullable<SportsMatch['liveScore']> }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 20_000);
    return () => clearInterval(timer);
  }, []);
  return <span className="match-clock">{clockText(score, now)}</span>;
}

const PERIOD_END: Record<string, number> = { '1H': 45, '2H': 90, ET: 120 };
const STATUS_TEXT: Record<string, MessageKey> = { HT: 'clock.ht', BT: 'clock.break', P: 'clock.penalties', FT: 'clock.ft', AET: 'clock.ft', PEN: 'clock.ft', SUSP: 'clock.paused', INT: 'clock.paused' };

export function clockText(score: NonNullable<SportsMatch['liveScore']>, now: number): string {
  const fixed = STATUS_TEXT[score.status];
  if (fixed) return t(fixed);
  if (score.minute === null) return t('clock.live');
  const minute = score.minute + Math.max(0, Math.floor((now - score.at) / 60_000));
  const end = PERIOD_END[score.status];
  return end !== undefined && minute > end ? `${end}+'` : `${minute}'`;
}

function When({ match, long = false }: { match: SportsMatch; long?: boolean }) {
  if (match.live && match.liveScore && !long) {
    return (
      <>
        <span className="sports-live">{t('clock.live')}</span>
        <span className="sports-minute">
          <MatchClock score={match.liveScore} />
        </span>
      </>
    );
  }
  if (match.live) return <span className="sports-live">{t('clock.live')}</span>;
  if (!match.startsAt) return <>{t('home.today')}</>;
  const time = new Date(match.startsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (!long) return <>{time}</>;
  if (match.scheduled) {
    return (
      <span className="slide__time">
        {dateText(match.startsAt)} · {time}
      </span>
    );
  }
  const until = untilKickoff(match.startsAt, Date.now());
  return (
    <span className="slide__time">
      {until === null
        ? `${dateText(match.startsAt)} · ${time}`
        : `${time} · ${'minutes' in until ? t('home.inMinutes', { count: until.minutes }) : t('home.inHours', { count: until.hours })}`}
    </span>
  );
}

/** How long until kick-off, as minutes or hours; null a day or more ahead, where the day says more than "in 140 h" (#191). */
export function untilKickoff(startsAt: number, now: number): { minutes: number } | { hours: number } | null {
  const minutes = Math.round((startsAt - now) / 60000);
  if (minutes < 60) return { minutes: Math.max(1, minutes) };
  const hours = Math.round(minutes / 60);
  return hours < 24 ? { hours } : null;
}

/** Not live, nothing playable yet, but channels it is expected on (#111). */
export function hasExpected(match: SportsMatch): boolean {
  return !match.live && match.channels.length === 0 && (match.upcoming?.length ?? 0) > 0;
}

/** "Expected on Sky Sports Football +2 · not checked yet", or null when there is nothing to expect. */
export function expectedText(match: SportsMatch): string | null {
  if (!hasExpected(match)) return null;
  const [first, ...rest] = match.upcoming!;
  return rest.length === 0 ? t('home.expectedOn', { name: first!.label }) : t('home.expectedOnMore', { name: first!.label, count: rest.length });
}

/** Live with nothing playable, but channels that might have it: "Might be on EPL MANCHESTER CITY [UK] +2 · search for them". */
export function mightBeOnText(match: SportsMatch): string | null {
  if (!match.live || match.checking || match.channels.length > 0 || !match.upcoming?.length) return null;
  const [first, ...rest] = match.upcoming;
  return rest.length === 0 ? t('home.mightBeOn', { name: first!.label }) : t('home.mightBeOnMore', { name: first!.label, count: rest.length });
}

function ChannelCount({ match }: { match: SportsMatch }) {
  const expected = expectedText(match) ?? mightBeOnText(match);
  if (expected) return <>{expected}</>;
  if (match.scheduled) return <>{match.startsAt !== null && dayStart(match.startsAt) === dayStart(Date.now()) ? t('home.channelsAtKickoff') : t('home.channelsOnMatchDay')}</>;
  if (match.checking) return <>{t('home.checking')}</>;
  if (match.channels.length === 0) return <>{match.live ? t('home.noChannel') : t('home.channelsAtKickoff')}</>;
  const languages = new Set(match.channels.map((channel) => channel.language ?? 'Other')).size;
  return (
    <>
      {t(match.channels.length === 1 ? 'home.oneChannel' : 'home.channels', { count: match.channels.length })} · {t(languages === 1 ? 'home.oneLanguage' : 'home.languages', { count: languages })}
    </>
  );
}

/** The channels a match will be on (or, live, might be on), the viewer's languages first. */
export function sortedUpcoming(upcoming: SportsMatch['upcoming'], preferred: string[]): NonNullable<SportsMatch['upcoming']> {
  const rank = (language: string | null) => (language !== null && preferred.includes(language) ? preferred.indexOf(language) : preferred.length);
  return [...(upcoming ?? [])].sort((a, b) => rank(a.language) - rank(b.language));
}

/** Names of the channels an upcoming match will be on, the viewer's languages first. */
export function upcomingNames(upcoming: SportsMatch['upcoming'], preferred: string[]): string[] {
  return sortedUpcoming(upcoming, preferred).map((channel) => channel.label);
}

/** The live match's channels that might have it and can be tried: as playable channels (Abel, 2026-10-28). */
export function mightHaveChannels(match: SportsMatch, preferred: string[]): SportsChannel[] {
  if (!match.live) return [];
  return sortedUpcoming(match.upcoming, preferred).flatMap((channel: Upcoming) =>
    typeof channel.streamId === 'number'
      ? [{ streamId: channel.streamId, label: channel.label, logo: channel.logo ?? null, language: channel.language, quality: channel.quality ?? null, ...(channel.variants ? { variants: channel.variants } : {}) }]
      : [],
  );
}

/** Pick a language, then a channel; next/previous in the player go through that language's channels. */
function MatchChannels({
  match,
  serverUrl,
  languages: preferred,
  inline,
  onClose,
  onSearch,
}: {
  match: SportsMatch;
  serverUrl: string;
  languages: string[];
  /** Under the match's own row: no title (the row shows it), Close at the end. */
  inline: boolean;
  onClose: () => void;
  /** Opens Search ("Search for more"). */
  onSearch?: () => void;
}) {
  const play = usePlay();
  const { sources, catalog } = useCatalog();
  // More channels from the viewer's own list: the tests can miss working ones (Abel, 2026-10-31). Not for the coming days.
  const [extra, setExtra] = useState<CatalogItem[]>([]);
  useEffect(() => {
    if (match.scheduled) return undefined;
    let active = true;
    void findExtraChannels((query) => catalog.search(query), match, preferred).then((found) => active && setExtra(found), () => undefined);
    return () => {
      active = false;
    };
    // The match object is rebuilt on every refresh; its channels decide what is already listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, sources, match.id, match.scheduled, match.channels.length, match.upcoming?.length, preferred]);
  const languages = useMemo(() => {
    const counts = new Map<string, number>();
    for (const channel of match.channels) counts.set(channel.language ?? 'Other', (counts.get(channel.language ?? 'Other') ?? 0) + 1);
    // The viewer's languages first, then by how many channels.
    const rank = (language: string) => (preferred.includes(language) ? preferred.indexOf(language) : preferred.length);
    return [...counts.entries()].sort((a, b) => rank(a[0]) - rank(b[0]) || b[1] - a[1]).map(([language]) => language);
  }, [match.channels, preferred]);
  const [language, setLanguage] = useState(() => startLanguage(match, preferred) ?? 'Other');
  const channels = channelsIn(match, language);
  const upcoming = sortedUpcoming(match.upcoming, preferred);
  const tryable = mightHaveChannels(match, preferred);
  const sourceId = sources?.[0]?.id ?? 'sports';

  const startIn = (list: SportsChannel[], index: number) => {
    const items = list.map((channel) => channelItem(serverUrl, sourceId, match, channel));
    play({ item: items[index]!, queue: { items, index } });
  };
  const start = (index: number) => startIn(channels, index);

  return (
    <div className={`sports-channels${inline ? ' sports-channels--inline' : ' panel'}`} role="region" aria-label={`${match.home} vs ${match.away}`}>
      {inline ? null : (
        <div className="sports-channels__head">
          <div>
            <strong>
              {match.home} vs {match.away}
            </strong>
            <div className="muted">
              {match.unconfirmed ? t('home.notConfirmed') : match.competition} · <When match={match} />
            </div>
          </div>
          <button type="button" className="button ghost" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      )}
      {match.channels.length === 0 && !match.checking && upcoming.length > 0 ? null : match.channels.length === 0 ? (
        <p className="muted">
          {match.checking
            ? t('home.checkingLong')
            : t('home.atKickoffLong')}
        </p>
      ) : (
        <>
          <div className="segmented" role="tablist" aria-label={t('home.commentary')}>
            {languages.map((name) => (
              <button key={name} type="button" role="tab" className="segmented__item" aria-selected={name === language} onClick={() => setLanguage(name)}>
                {commentary(name)}
              </button>
            ))}
          </div>
          <ul className="sports-channels__list">
            {channels.map((channel, index) => (
              <li key={channel.streamId}>
                <button type="button" className="sports-channel" onClick={() => start(index)}>
                  <span className="sports-channel__name">{channel.label}</span>
                  {channel.quality ? <span className="badge">{channel.quality}</span> : null}
                  {channel.shared ? (
                    <span className="sports-channel__shared">{t('home.mayShowOther')}</span>
                  ) : channel.confirmed ? (
                    <span className="sports-channel__ok">{t('home.confirmedByViewers')}</span>
                  ) : (
                    <span className="sports-channel__ok">{(channel.watching ?? 0) > 0 ? t('home.watchedNow') : t('home.playsSmoothly')}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {upcoming.length > 0 ? (
        <>
          <p className="muted">{t(match.live ? 'home.mightHave' : 'home.willBeOn')}</p>
          {/* Numbered feeds of a broadcaster (Liga de Campeones 1–8, Sky Sports+ 1, 2…): which one has the match isn't known yet (#156). */}
          {upcoming.some((channel) => channel.shared) ? <p className="muted">{t(match.live ? 'home.guessesLive' : 'home.guessesBefore')}</p> : null}
          <ul className="sports-channels__list">
            {upcoming.map((channel) => {
              const index = tryable.findIndex((candidate) => candidate.streamId === channel.streamId);
              return index >= 0 ? (
                <li key={channel.label}>
                  <button type="button" className="sports-channel" onClick={() => startIn(tryable, index)}>
                    <span className="sports-channel__name">{channel.label}</span>
                    {channel.quality ? <span className="badge">{channel.quality}</span> : null}
                    <span className="sports-channel__shared">{t('home.mightHaveShort')}</span>
                  </button>
                </li>
              ) : (
                <li key={channel.label} className="sports-upcoming">
                  {channel.label}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
      {!match.scheduled ? (
        <div className="sports-extra">
          <p className="muted">{t('home.moreToTry')}</p>
          {extra.length > 0 ? (
            <ul className="sports-channels__list">
              {extra.map((item, index) => (
                <li key={`${item.sourceId}:${item.key}`}>
                  <button type="button" className="sports-channel" onClick={() => play({ item, queue: { items: extra, index } })}>
                    <span className="sports-channel__name">{item.name}</span>
                    <span className="sports-channel__shared">{t('home.notChecked')}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {onSearch ? (
            <>
              {/* Which channels to look for: Premier League+ and Sky Sports+ for the Premier League (Abel, 2026-10-31). */}
              <p className="muted">
                {searchHintNames(match).length > 0 ? t('home.searchHint', { channels: searchHintNames(match).join(', ') }) : t('home.searchHintClubs')}
              </p>
              <button type="button" className="button ghost" onClick={onSearch}>
                {t('home.searchMore')}
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {inline ? (
        <div className="sports-channels__foot">
          <button type="button" className="button ghost" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** A tested channel as a playable live item (the line pool's placeholder login, filled in by the relay). */
function channelItem(serverUrl: string, sourceId: string, match: SportsMatch, channel: SportsChannel): CatalogItem {
  const item: CatalogItem = {
    sourceId,
    key: `l${channel.streamId}`,
    kind: 'live',
    categoryKey: '',
    ordCat: 0,
    ordAll: 0,
    name: `${match.home} vs ${match.away} · ${channel.label}`,
    logo: channel.logo,
    url: `${serverUrl.replace(/\/+$/, '')}/live/pool/pool/${channel.streamId}.ts`,
    data: { streamId: channel.streamId, sportsMatch: { id: match.id, title: `${match.home} vs ${match.away}` } },
    hash: '',
  };
  // Its other qualities, for the player's quality menu.
  const variants = channel.variants?.map(({ streamId, label, quality }) => ({ name: label, quality, url: `${serverUrl.replace(/\/+$/, '')}/live/pool/pool/${streamId}.ts` }));
  return variants ? { ...item, data: { ...item.data, variants } } : item;
}
